import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Checkbox, DatePicker, Empty, Form, Input, List, Modal, Select, Space, Spin, Tag, Typography, message } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import updateLocale from 'dayjs/plugin/updateLocale';
import { api } from './api';
import { formatDateTimeFull, formatDateTimeShort } from './constants';
import type { CalendarEvent } from './types';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import 'dayjs/locale/da';

dayjs.locale('da');
dayjs.extend(updateLocale);
dayjs.extend(isoWeek);
dayjs.updateLocale('da', {
  weekStart: 1
});

const { Title, Text } = Typography;

type CalendarPageProps = {
  availableLeads: Array<{ id: string; company_name: string }>;
  onOpenLead: (leadId: string) => void;
};
type RawCalendarEvent = {
  id: string;
  title?: string | null;
  start_at?: string | null;
  end_at?: string | null;
  location?: string | null;
  notes?: string | null;
  lead_id?: string | null;
  lead_company_name?: string | null;
  source?: string | null;
  created_at?: string | null;
  created_by?: string | null;
};

const LEAD_EVENT_COLOR = '#faad14';
const MANUAL_EVENT_COLOR = '#1890ff';

function toCalendarEvent(raw: RawCalendarEvent): CalendarEvent {
  return {
    id: raw.id,
    title: raw.title ?? null,
    startAt: raw.start_at ?? null,
    endAt: raw.end_at ?? null,
    location: raw.location ?? null,
    notes: raw.notes ?? null,
    leadId: raw.lead_id ?? null,
    leadCompanyName: raw.lead_company_name ?? null,
    source: raw.source === 'LEAD_MEETING' ? 'LEAD_MEETING' : 'CALENDAR',
    createdAt: raw.created_at ?? null,
    createdBy: raw.created_by ?? null
  };
}

function getRangeForWindow(anchor: Dayjs) {
  const start = anchor.startOf('month').startOf('day');
  const end = start.add(2, 'month').startOf('day');
  return { from: start, to: end };
}

export default function CalendarPage({ availableLeads, onOpenLead }: CalendarPageProps) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(() => dayjs().startOf('day'));
  const [viewStart, setViewStart] = useState<Dayjs>(() => dayjs().startOf('month'));
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [editOpen, setEditOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [hideEmptyDays, setHideEmptyDays] = useState(false);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      if (!event.startAt) {
        continue;
      }
      const key = dayjs(event.startAt).format('YYYY-MM-DD');
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(event);
    }
    for (const [key, items] of map.entries()) {
      items.sort((a, b) => {
        const aTime = a.startAt ? dayjs(a.startAt).valueOf() : 0;
        const bTime = b.startAt ? dayjs(b.startAt).valueOf() : 0;
        if (aTime === bTime) {
          return (a.title || '').localeCompare(b.title || '', 'da', { sensitivity: 'base' });
        }
        return aTime - bTime;
      });
      map.set(key, items);
    }
    return map;
  }, [events]);

  const selectedKey = selectedDate.format('YYYY-MM-DD');
  const selectedEvents = eventsByDate.get(selectedKey) ?? [];

  const viewEnd = useMemo(() => viewStart.add(2, 'month').subtract(1, 'day'), [viewStart]);

  const daysInRange = useMemo<Array<{ day: Dayjs; events: CalendarEvent[] }>>(() => {
    const start = viewStart.startOf('day');
    const end = viewEnd.startOf('day');
    const entries: Array<{ day: Dayjs; events: CalendarEvent[] }> = [];
    let cursor = start;
    while (cursor.isBefore(end, 'day') || cursor.isSame(end, 'day')) {
      const key = cursor.format('YYYY-MM-DD');
      entries.push({ day: cursor, events: eventsByDate.get(key) ?? [] });
      cursor = cursor.add(1, 'day').startOf('day');
    }
    return entries;
  }, [viewStart, viewEnd, eventsByDate]);

  const visibleDays = useMemo(() => {
    if (!hideEmptyDays) {
      return daysInRange;
    }
    return daysInRange.filter(({ events }) => events.length > 0);
  }, [daysInRange, hideEmptyDays]);

  const loadEvents = useCallback(async (reference: Dayjs) => {
    const { from, to } = getRangeForWindow(reference);
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('from', from.toISOString());
      params.set('to', to.toISOString());
      const res = await api(`/api/calendar?${params.toString()}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente kalenderaftaler');
      }
      const data = (await res.json()) as RawCalendarEvent[];
      setEvents(data.map(toCalendarEvent));
    } catch (error: any) {
      console.error('Failed to load calendar events', error);
      message.error(error?.message || 'Kunne ikke hente kalenderaftaler');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadEvents(viewStart);
  }, [viewStart, loadEvents]);

  useEffect(() => {
    if (selectedDate.isBefore(viewStart, 'day') || selectedDate.isAfter(viewEnd, 'day')) {
      setSelectedDate(viewStart.startOf('day'));
    }
  }, [viewStart, viewEnd, selectedDate]);

  const handleRefresh = useCallback(() => {
    void loadEvents(viewStart);
  }, [viewStart, loadEvents]);

  const handleSelectDay = useCallback((value: Dayjs) => {
    setSelectedDate(value.startOf('day'));
  }, []);

  const handleDatePickerChange = useCallback((value: Dayjs | null) => {
    if (!value) {
      return;
    }
    setViewStart(value.startOf('month'));
    setSelectedDate(value.startOf('day'));
  }, []);

  const goToPreviousMonth = useCallback(() => {
    setViewStart((prev) => prev.subtract(1, 'month'));
  }, []);

  const goToNextMonth = useCallback(() => {
    setViewStart((prev) => prev.add(1, 'month'));
  }, []);

  const goToCurrentMonth = useCallback(() => {
    const now = dayjs();
    setViewStart(now.startOf('month'));
    setSelectedDate(now.startOf('day'));
  }, []);

  const openCreateModal = useCallback(() => {
    const defaultStart = selectedDate.hour(9).minute(0).second(0).millisecond(0);
    form.setFieldsValue({
      title: '',
      start_at: defaultStart,
      end_at: null,
      location: '',
      notes: '',
      lead_id: undefined
    });
    setCreateOpen(true);
  }, [form, selectedDate]);

  const closeCreateModal = useCallback(() => {
    setCreateOpen(false);
    form.resetFields();
  }, [form]);

  const openEditModal = useCallback(
    (event: CalendarEvent) => {
      if (event.source !== 'CALENDAR') {
        message.info('Lead-møder redigeres direkte på leadet.');
        return;
      }
      setEditingEvent(event);
      setEditOpen(true);
    },
    []
  );

  const closeEditModal = useCallback(() => {
    setEditOpen(false);
    setEditingEvent(null);
    editForm.resetFields();
  }, [editForm]);

  useEffect(() => {
    if (editOpen && editingEvent) {
      editForm.setFieldsValue({
        title: editingEvent.title || '',
        start_at: editingEvent.startAt ? dayjs(editingEvent.startAt) : null,
        end_at: editingEvent.endAt ? dayjs(editingEvent.endAt) : null,
        location: editingEvent.location || '',
        notes: editingEvent.notes || '',
        lead_id: editingEvent.leadId || undefined
      });
    } else {
      editForm.resetFields();
    }
  }, [editOpen, editingEvent, editForm]);

  const handleCreate = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const payload: Record<string, unknown> = {
        title: typeof values.title === 'string' ? values.title.trim() : ''
      };

      const startValue: Dayjs | undefined = values.start_at;
      if (!startValue || !dayjs.isDayjs(startValue)) {
        message.error('Vælg et starttidspunkt');
        return;
      }
      payload.start_at = startValue.toISOString();

      const endValue: Dayjs | undefined = values.end_at;
      if (endValue && dayjs.isDayjs(endValue)) {
        payload.end_at = endValue.toISOString();
      }

      if (values.location && typeof values.location === 'string') {
        const trimmed = values.location.trim();
        if (trimmed) {
          payload.location = trimmed;
        }
      }

      if (values.notes && typeof values.notes === 'string') {
        const trimmed = values.notes.trim();
        if (trimmed) {
          payload.notes = trimmed;
        }
      }

      if (values.lead_id && typeof values.lead_id === 'string') {
        payload.lead_id = values.lead_id;
      }

      setCreating(true);
      const res = await api('/api/calendar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke oprette kalenderaftale');
      }

      message.success('Kalenderaftale oprettet');
      closeCreateModal();
    await loadEvents(viewStart);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke oprette kalenderaftale');
    } finally {
      setCreating(false);
    }
  }, [form, closeCreateModal, loadEvents, viewStart]);

  const handleUpdate = useCallback(async () => {
    if (!editingEvent) {
      return;
    }
    try {
      const values = await editForm.validateFields();
      const payload: Record<string, unknown> = {
        title: typeof values.title === 'string' ? values.title.trim() : ''
      };

      const startValue: Dayjs | undefined = values.start_at;
      if (!startValue || !dayjs.isDayjs(startValue)) {
        message.error('Vælg et starttidspunkt');
        return;
      }
      payload.start_at = startValue.toISOString();

      const endValue: Dayjs | undefined = values.end_at;
      if (endValue && dayjs.isDayjs(endValue)) {
        payload.end_at = endValue.toISOString();
      } else {
        payload.end_at = null;
      }

      const normalizeField = (value: unknown) => {
        if (typeof value !== 'string') {
          return null;
        }
        const trimmed = value.trim();
        return trimmed || null;
      };

      payload.location = normalizeField(values.location);
      payload.notes = normalizeField(values.notes);
      payload.lead_id = typeof values.lead_id === 'string' ? values.lead_id : null;

      setUpdating(true);
      const res = await api(`/api/calendar/${editingEvent.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke opdatere kalenderaftalen');
      }
      message.success('Kalenderaftale opdateret');
      closeEditModal();
      await loadEvents(viewStart);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke opdatere kalenderaftalen');
    } finally {
      setUpdating(false);
    }
  }, [editForm, editingEvent, closeEditModal, loadEvents, viewStart]);

  const handleDelete = useCallback(
    async (eventId: string) => {
      setDeletingId(eventId);
      try {
        const res = await api(`/api/calendar/${eventId}`, { method: 'DELETE' });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke slette kalenderaftalen');
        }
        message.success('Kalenderaftale slettet');
        if (editingEvent?.id === eventId) {
          closeEditModal();
        }
        await loadEvents(viewStart);
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke slette kalenderaftalen');
      } finally {
        setDeletingId(null);
      }
    },
    [editingEvent, closeEditModal, loadEvents, viewStart]
  );

  const confirmDelete = useCallback(
    (event: CalendarEvent) => {
      if (event.source !== 'CALENDAR') {
        message.info('Lead-møder slettes direkte på leadet.');
        return;
      }
      Modal.confirm({
        title: 'Slet kalenderaftale',
        content: `Er du sikker på, at du vil slette "${event.title || 'aftalen'}"?`,
        okType: 'danger',
        okText: 'Slet',
        cancelText: 'Annuller',
        onOk: () => handleDelete(event.id)
      });
    },
    [handleDelete]
  );

  return (
    <Space
      className="calendar-page"
      direction="vertical"
      size={24}
      style={{ width: '100%', alignItems: 'stretch' }}
    >
      <Card
        style={{ width: '100%' }}
        title={
          <Space direction="vertical" size={0}>
            <Text strong>{selectedDate.format('dddd [den] D. MMMM YYYY')}</Text>
            <Text type="secondary">{selectedEvents.length} aftaler</Text>
          </Space>
        }
        extra={
          <Space size={8}>
            <Button onClick={handleRefresh} disabled={loading}>
              Opdater
            </Button>
            <Button type="primary" onClick={openCreateModal}>
              Ny aftale
            </Button>
          </Space>
        }
        bodyStyle={{ padding: 0 }}
      >
        {selectedEvents.length === 0 ? (
          <Empty description="Ingen aftaler" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '48px 0' }} />
        ) : (
          <List
            dataSource={selectedEvents}
            renderItem={(event) => {
              const startLabel = formatDateTimeShort(event.startAt) || 'Ukendt tidspunkt';
              const fullLabel = formatDateTimeFull(event.startAt) || undefined;
              return (
                <List.Item key={`${event.source}-${event.id}`} style={{ alignItems: 'flex-start', padding: '12px 16px' }}>
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Space align="center" size={8} wrap>
                      <Tag color={event.source === 'LEAD_MEETING' ? LEAD_EVENT_COLOR : MANUAL_EVENT_COLOR}>
                        {event.source === 'LEAD_MEETING' ? 'Lead-møde' : 'Kalender'}
                      </Tag>
                      <Text strong>{event.title || (event.source === 'LEAD_MEETING' ? 'Lead-møde' : 'Aftale')}</Text>
                    </Space>
                    <Text type="secondary" title={fullLabel}>{startLabel}</Text>
                    {event.location ? <Text>{event.location}</Text> : null}
                    {event.notes ? <Text type="secondary">{event.notes}</Text> : null}
                    {event.leadId && (
                      <Button type="link" size="small" onClick={() => onOpenLead(event.leadId!)}>
                        Åbn lead {event.leadCompanyName ? `(${event.leadCompanyName})` : ''}
                      </Button>
                    )}
                                {event.source === 'CALENDAR' ? (
                                  <Space size={4}>
                                    <Button
                                      type="link"
                                      size="small"
                                      onClick={() => openEditModal(event)}
                                    >
                                      Rediger
                                    </Button>
                                    <Button
                                      type="link"
                                      danger
                                      size="small"
                                      loading={deletingId === event.id}
                                      onClick={() => confirmDelete(event)}
                                    >
                                      Slet
                                    </Button>
                                  </Space>
                                ) : null}
                  </Space>
                </List.Item>
              );
            }}
          />
        )}
      </Card>

      <Card
        style={{ width: '100%' }}
        title={<Title level={5} style={{ margin: 0 }}>Kalender</Title>}
        extra={
          <Space size={8} wrap>
            <Button icon={<LeftOutlined />} onClick={goToPreviousMonth} aria-label="Forrige måned" />
            <Button onClick={goToCurrentMonth}>I dag</Button>
            <Button icon={<RightOutlined />} onClick={goToNextMonth} aria-label="Næste måned" />
            <DatePicker
              value={viewStart}
              onChange={handleDatePickerChange}
              picker="month"
              format="MMMM YYYY"
              allowClear={false}
            />
            <Checkbox checked={hideEmptyDays} onChange={(event) => setHideEmptyDays(event.target.checked)}>
              Skjul tomme dage
            </Checkbox>
          </Space>
        }
        bodyStyle={{ padding: 0 }}
      >
        <Spin spinning={loading} tip="Henter aftaler..." className="calendar-table-spinner">
          <div className="calendar-table-wrapper">
            {visibleDays.length === 0 ? (
              <Empty description="Ingen datoer" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '48px 0' }} />
            ) : (
              <table
                className="calendar-table"
                style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', borderBottom: '1px solid #d9d9d9' }}
              >
                <colgroup>
                  <col className="calendar-table-col-date" />
                  <col className="calendar-table-col-events" />
                </colgroup>
                <tbody>
                  {visibleDays.map(({ day, events: dayEvents }) => {
                    const isSelected = day.isSame(selectedDate, 'day');
                    const isToday = day.isSame(dayjs(), 'day');
                    const rowKey = day.format('YYYY-MM-DD');
                    const rowStyle = { borderBottom: '1px solid #d9d9d9', padding: 0 } as const;
                    const dateCellStyle = {
                      width: '33%',
                      minWidth: '33%',
                      verticalAlign: 'top',
                      padding: '28px 32px',
                      borderBottom: '1px solid #d9d9d9'
                    } as const;
                    const eventCellStyle = {
                      width: '67%',
                      minWidth: '67%',
                      verticalAlign: 'top',
                      padding: '28px 32px',
                      borderBottom: '1px solid #d9d9d9'
                    } as const;
                    return (
                      <tr
                        key={rowKey}
                        className={`calendar-table-row${isSelected ? ' is-selected' : ''}`}
                        onClick={() => handleSelectDay(day)}
                        tabIndex={0}
                        aria-selected={isSelected}
                        style={rowStyle}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleSelectDay(day);
                          }
                        }}
                      >
                      <td
                        className="calendar-table-cell calendar-table-cell--date"
                        style={dateCellStyle}
                      >
                        <div className="calendar-row__date">
                          <div className="calendar-row__date-week">
                            <Tag color="blue">Uge {day.isoWeek()}</Tag>
                          </div>
                          <div className="calendar-row__date-label">
                            <Text strong className={isToday ? 'is-today' : undefined}>
                              {day.format('D. MMMM YYYY')}
                            </Text>
                          </div>
                          <div className="calendar-row__date-weekday">
                            <Text type="secondary">{day.format('dddd')}</Text>
                          </div>
                          <div className="calendar-row__date-count">
                            <Text type="secondary">{dayEvents.length} aftaler</Text>
                          </div>
                        </div>
                      </td>
                      <td
                        className="calendar-table-cell calendar-table-cell--events"
                        style={eventCellStyle}
                      >
                        <div className="calendar-row__events">
                          {dayEvents.length === 0 ? (
                            <Text type="secondary">Ingen aftaler</Text>
                          ) : (
                            <div className="calendar-events">
                              {dayEvents.map((event) => {
                                const startLabel = formatDateTimeShort(event.startAt) || 'Ukendt tidspunkt';
                                const fullLabel = formatDateTimeFull(event.startAt) || undefined;
                                const isLeadMeeting = event.source === 'LEAD_MEETING';
                                return (
                                  <div key={`${event.source}-${event.id}`} className="calendar-event-row">
                                    <div className="calendar-event-row__time">
                                      <Text type="secondary" title={fullLabel}>{startLabel}</Text>
                                    </div>
                                    <div className="calendar-event-row__details">
                                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                        <Space align="center" size={8} wrap>
                                          <Tag color={isLeadMeeting ? LEAD_EVENT_COLOR : MANUAL_EVENT_COLOR}>
                                            {isLeadMeeting ? 'Lead-møde' : 'Kalender'}
                                          </Tag>
                                          <Text strong>{event.title || (isLeadMeeting ? 'Lead-møde' : 'Kalenderaftale')}</Text>
                                        </Space>
                                        {event.location ? <Text>{event.location}</Text> : null}
                                        {event.notes ? <Text type="secondary">{event.notes}</Text> : null}
                                        {event.leadId ? (
                                          <Button
                                            type="link"
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onOpenLead(event.leadId!);
                                            }}
                                          >
                                            Åbn lead {event.leadCompanyName ? `(${event.leadCompanyName})` : ''}
                                          </Button>
                                        ) : null}
                                        {event.source === 'CALENDAR' ? (
                                          <Space size={4}>
                                            <Button
                                              type="link"
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openEditModal(event);
                                              }}
                                            >
                                              Rediger
                                            </Button>
                                            <Button
                                              type="link"
                                              danger
                                              size="small"
                                              loading={deletingId === event.id}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                confirmDelete(event);
                                              }}
                                            >
                                              Slet
                                            </Button>
                                          </Space>
                                        ) : null}
                                      </Space>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </Spin>
      </Card>

      <Modal
        title="Ny kalenderaftale"
        open={createOpen}
        onCancel={closeCreateModal}
        onOk={handleCreate}
        confirmLoading={creating}
        destroyOnClose
      >
        <Form layout="vertical" form={form} preserve={false} autoComplete="off">
          <Form.Item
            name="title"
            label="Titel"
            rules={[{ required: true, message: 'Angiv en titel for aftalen' }]}
          >
            <Input placeholder="F.eks. Opfølgning" maxLength={120} />
          </Form.Item>
          <Form.Item
            name="start_at"
            label="Starttidspunkt"
            rules={[{ required: true, message: 'Vælg starttidspunkt' }]}
          >
            <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
          </Form.Item>
          <Form.Item name="end_at" label="Sluttidspunkt">
            <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
          </Form.Item>
          <Form.Item name="location" label="Lokation">
            <Input placeholder="F.eks. Teams eller adressen" maxLength={120} />
          </Form.Item>
          <Form.Item name="notes" label="Noter">
            <Input.TextArea rows={3} placeholder="Evt. noter" />
          </Form.Item>
          <Form.Item name="lead_id" label="Tilknyt lead">
            <Select
              allowClear
              placeholder="Vælg lead (valgfrit)"
              showSearch
              optionFilterProp="label"
              options={availableLeads.map((lead) => ({ value: lead.id, label: lead.company_name }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingEvent ? `Rediger "${editingEvent.title || 'aftale'}"` : 'Rediger aftale'}
        open={editOpen}
        forceRender
        onCancel={closeEditModal}
        onOk={handleUpdate}
        confirmLoading={updating}
        destroyOnClose
      >
        <Form layout="vertical" form={editForm} preserve={false} autoComplete="off">
          <Form.Item
            name="title"
            label="Titel"
            rules={[{ required: true, message: 'Angiv en titel for aftalen' }]}
          >
            <Input placeholder="F.eks. Opfølgning" maxLength={120} />
          </Form.Item>
          <Form.Item
            name="start_at"
            label="Starttidspunkt"
            rules={[{ required: true, message: 'Vælg starttidspunkt' }]}
          >
            <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
          </Form.Item>
          <Form.Item name="end_at" label="Sluttidspunkt">
            <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
          </Form.Item>
          <Form.Item name="location" label="Lokation">
            <Input placeholder="F.eks. Teams eller adressen" maxLength={120} />
          </Form.Item>
          <Form.Item name="notes" label="Noter">
            <Input.TextArea rows={3} placeholder="Evt. noter" />
          </Form.Item>
          <Form.Item name="lead_id" label="Tilknyt lead">
            <Select
              allowClear
              placeholder="Vælg lead (valgfrit)"
              showSearch
              optionFilterProp="label"
              options={availableLeads.map((lead) => ({ value: lead.id, label: lead.company_name }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
